import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { SiteFooter } from "../components/SiteFooter";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [plate, setPlate] = useState("");

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setState("timeout"); return; }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      setPlate(res.data.plate || "");
      if (res.data.payment_status === "paid") { setState("success"); return; }
      if (res.data.status === "expired") { setState("failed"); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    }
  }, []);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("failed"); return; }
    poll(sessionId);
  }, [poll]);

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-14 h-14 animate-spin text-volt mx-auto mb-6" />
            <h1 className="font-head font-extrabold text-3xl uppercase tracking-tight">Confirming Payment</h1>
            <p className="text-muted-foreground mt-3">Hang tight, activating your membership…</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="payment-success" className="reveal">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h1 className="font-head font-black text-4xl uppercase tracking-tight">Membership Active</h1>
            <p className="text-muted-foreground mt-3">{plate ? `${plate} is now covered for 30 days.` : "Your plate is now covered for 30 days."}</p>
            <button data-testid="payment-to-dashboard" onClick={() => navigate("/dashboard")}
              className="mt-8 bg-volt text-white px-8 py-3 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors">Go To Dashboard</button>
          </div>
        )}
        {(state === "failed" || state === "timeout") && (
          <div data-testid="payment-failed">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-head font-extrabold text-3xl uppercase tracking-tight">{state === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
            <p className="text-muted-foreground mt-3">{state === "timeout" ? "Check your dashboard shortly to confirm." : "No charge was made. Please try again."}</p>
            <button onClick={() => navigate("/dashboard")} className="mt-8 border border-white/15 px-8 py-3 overline hover:border-volt hover:text-white transition-colors">Back To Dashboard</button>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
